import { TextareaHTMLAttributes, forwardRef, useState, useEffect, useRef } from 'react';
import { cn } from '../utils/cn';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  autoResize?: boolean;
  showCharacterCount?: boolean;
  maxLength?: number;
}

/**
 * Textarea - Accessible textarea component with auto-resize and character counter
 * 
 * Required CSS classes:
 * - .textarea-wrapper - Wrapper container
 * - .textarea-label - Label styles
 * - .textarea - Base textarea styles
 * - .textarea-error - Error state styles
 * - .textarea-hint - Hint text styles
 * - .textarea-error-message - Error message styles
 * - .textarea-character-count - Character count styles
 * 
 * Accessibility:
 * - Keyboard: Tab (focus), standard text editing
 * - Screen reader: Announces label, error, and character count
 * - Focus: Visible focus indicator
 * 
 * @example
 * <Textarea 
 *   label="Description" 
 *   autoResize 
 *   showCharacterCount 
 *   maxLength={500}
 * />
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      label,
      error,
      hint,
      id,
      autoResize = false,
      showCharacterCount = false,
      maxLength,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const [characterCount, setCharacterCount] = useState(() => {
      if (typeof value === 'string') return value.length;
      return 0;
    });

    // Combine refs
    const combinedRef = (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    // Auto-resize functionality
    useEffect(() => {
      if (!autoResize || !textareaRef.current) return;

      const textarea = textareaRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, [autoResize, value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (showCharacterCount || maxLength) {
        setCharacterCount(e.target.value.length);
      }
      onChange?.(e);
    };

    const textareaId = id || props.name;
    const currentLength = typeof value === 'string' ? value.length : characterCount;
    const isAtLimit = maxLength ? currentLength >= maxLength : false;

    return (
      <div className="textarea-wrapper">
        {label && (
          <label htmlFor={textareaId} className="textarea-label">
            {label}
          </label>
        )}
        <textarea
          ref={combinedRef}
          id={textareaId}
          className={cn('textarea', error && 'textarea-error', className)}
          aria-invalid={!!error}
          aria-describedby={
            error
              ? `${textareaId}-error`
              : hint
              ? `${textareaId}-hint`
              : showCharacterCount
              ? `${textareaId}-count`
              : undefined
          }
          maxLength={maxLength}
          value={value}
          onChange={handleChange}
          {...props}
        />
        {(error || hint || showCharacterCount) && (
          <div className="textarea-footer">
            {error && (
              <p id={`${textareaId}-error`} className="textarea-error-message" role="alert">
                {error}
              </p>
            )}
            {hint && !error && (
              <p id={`${textareaId}-hint`} className="textarea-hint">
                {hint}
              </p>
            )}
            {showCharacterCount && (
              <span
                id={`${textareaId}-count`}
                className={cn(
                  'textarea-character-count',
                  isAtLimit && 'textarea-character-count-limit'
                )}
                aria-live="polite"
              >
                {currentLength}
                {maxLength && ` / ${maxLength}`}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
